import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  AppBar, 
  Box, 
  CssBaseline, 
  Divider, 
  Drawer, 
  IconButton, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  Toolbar, 
  Typography, 
  Menu, 
  MenuItem, 
  Avatar,
  useTheme,
  useMediaQuery,
  Fade,
  Chip
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  ViewList as TableIcon,
  SwapHoriz as TransferIcon,
  Group as UserIcon,
  Assignment as AuditIcon,
  Person as PersonIcon,
  ExitToApp as LogoutIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { selectUser, logout } from '../store/slices/authSlice';
import { AppDispatch } from '../store';

const drawerWidth = 260;

export default function MainLayout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector(selectUser);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const isAdmin = user?.roles.some(role => role.name === 'ADMIN');
  const isFarmer = user?.roles.some(role => role.name === 'FARMER');
  const isDeveloper = user?.roles.some(role => role.name === 'DEVELOPER');
  const isPM = user?.roles.some(role => role.name === 'PM');

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const menuItems = [
    { 
      text: 'Дашборд', 
      icon: <DashboardIcon />, 
      path: '/dashboard',
      visible: true,
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    { 
      text: 'Таблицы данных', 
      icon: <TableIcon />, 
      path: '/tables',
      visible: true,
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
    },
    { 
      text: 'Передача данных', 
      icon: <TransferIcon />, 
      path: '/transfers',
      visible: true,
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
    },
    { 
      text: 'Пользователи', 
      icon: <UserIcon />, 
      path: '/users',
      visible: isAdmin,
      gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
    },
    { 
      text: 'Аудит', 
      icon: <AuditIcon />, 
      path: '/audit',
      visible: isAdmin,
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'
    },
  ];

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          p: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            right: '-50%',
            width: '100%',
            height: '100%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
          }
        }}
      >
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 700,
            position: 'relative',
            zIndex: 1,
            textShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          Управление
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            opacity: 0.9,
            position: 'relative',
            zIndex: 1
          }}
        >
          аккаунтами
        </Typography>
      </Box>
      
      <List sx={{ px: 2, py: 2, flexGrow: 1 }}>
        {menuItems.filter(item => item.visible).map((item, index) => (
          <Fade in key={item.text} timeout={300 + index * 100}>
            <ListItem disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) {
                    setMobileOpen(false);
                  }
                }}
                sx={{
                  borderRadius: 2,
                  transition: 'all 0.3s',
                  position: 'relative',
                  overflow: 'hidden',
                  ...(isActive(item.path) ? {
                    background: item.gradient,
                    color: 'white',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: '4px',
                      background: 'white',
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'white',
                    }
                  } : {
                    '&:hover': {
                      background: `${item.gradient.replace('100%', '10%')}`,
                      transform: 'translateX(4px)',
                    }
                  })
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    transition: 'all 0.3s',
                    ...(isActive(item.path) && {
                      transform: 'scale(1.1)',
                    })
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: isActive(item.path) ? 600 : 500,
                  }}
                />
              </ListItemButton>
            </ListItem>
          </Fade>
        ))}
      </List>

      <Divider sx={{ mx: 2 }} />
      
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
            border: '1px solid rgba(102, 126, 234, 0.2)',
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
            Вы вошли как:
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {user?.fullName}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
            {user?.roles.map((role) => (
              <Chip
                key={role.id}
                label={role.name}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f7fa' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          background: 'white',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              mr: 2, 
              display: { sm: 'none' },
              color: '#667eea',
            }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography 
            variant="h6" 
            noWrap 
            component="div" 
            sx={{ 
              flexGrow: 1,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 700,
            }}
          >
            Система управления аккаунтами
          </Typography>

          <IconButton
            size="large"
            sx={{
              mr: 2,
              color: '#667eea',
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'scale(1.1)',
              }
            }}
          >
          </IconButton>
          
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            sx={{
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'scale(1.05)',
              }
            }}
          >
            <Avatar 
              sx={{ 
                width: 40, 
                height: 40,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                fontWeight: 600,
              }}
            >
              {user?.fullName.charAt(0) || 'U'}
            </Avatar>
          </IconButton>
          
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              sx: {
                mt: 1.5,
                borderRadius: 2,
                minWidth: 220,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                }
              }
            }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                {user?.email}
              </Typography>
            </Box>
            
            <MenuItem 
              onClick={() => {
                handleProfileMenuClose();
                navigate('/profile');
              }}
              sx={{
                py: 1.5,
                '&:hover': {
                  bgcolor: 'rgba(102, 126, 234, 0.08)',
                }
              }}
            >
              <ListItemIcon>
                <PersonIcon fontSize="small" sx={{ color: '#667eea' }} />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontWeight: 500 }}>
                Профиль
              </ListItemText>
            </MenuItem>
            
            <MenuItem 
              onClick={() => {
                handleProfileMenuClose();
                navigate('/settings');
              }}
              sx={{
                py: 1.5,
                '&:hover': {
                  bgcolor: 'rgba(102, 126, 234, 0.08)',
                }
              }}
            >
              <ListItemIcon>
                <SettingsIcon fontSize="small" sx={{ color: '#667eea' }} />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontWeight: 500 }}>
                Настройки
              </ListItemText>
            </MenuItem>
            
            <Divider />
            
            <MenuItem 
              onClick={() => {
                handleProfileMenuClose();
                handleLogout();
              }}
              sx={{
                py: 1.5,
                color: 'error.main',
                '&:hover': {
                  bgcolor: 'rgba(244, 67, 54, 0.08)',
                }
              }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" sx={{ color: 'error.main' }} />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontWeight: 500 }}>
                Выход
              </ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              borderRight: 'none',
            },
          }}
        >
          <Box sx={{ position: 'relative', height: '100%' }}>
            <IconButton
              onClick={handleDrawerToggle}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                zIndex: 1,
                color: 'white',
              }}
            >
              <CloseIcon />
            </IconButton>
            {drawer}
          </Box>
        </Drawer>
        
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              borderRight: '1px solid rgba(0,0,0,0.08)',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box
        component="main"
        sx={{ 
          flexGrow: 1, 
          p: { xs: 2, sm: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: '64px',
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}